import type { ReactNode } from 'react'
import type { SidePreparation } from '../lib/verificationItems.ts'
import type { ProductionSheet as Sheet } from '../lib/productionSheet.ts'
import { ruleOutcomesFor, type RuleOutcomeRecord } from '../lib/ruleOutcomes.ts'
import { LabelPrint } from './LabelPrint.tsx'
import { ProductionSheetContent } from './ProductionSheet.tsx'
import { RuleOutcomeList } from './RuleOutcomes.tsx'

/** One preparation as the context sections show it. */
export interface ContextPreparation {
  readonly preparation: SidePreparation
  /** "1) Cápsula", said above its part when the sections hold more than one. */
  readonly title: string
  readonly labelLines?: readonly string[] | undefined
}

/**
 * The label, the production sheet and the operational rules verified, under
 * every step of the divergence-only pass (#641).
 *
 * **Always closed.** The step is about one divergence, and these are what
 * somebody opens to decide it, not what they read first. The caller keys this
 * by step, so a section opened on one step is closed again on the next.
 *
 * On a step about one preparation, that preparation's three. On a step about
 * none -- the patient, the prescriber, a rule about the requisition -- only
 * the labels, each under its pote's title: the label is where those names are
 * printed, and every pote's ficha and rules would be the potes with nothing to
 * answer that the pass exists to leave out. A section with nothing in it is
 * left out, as on the full pass.
 */
export function DivergenceContext({
  preparations,
  sheet,
  outcomes,
  labelsOnly = false,
}: {
  preparations: readonly ContextPreparation[]
  sheet: Sheet | undefined
  outcomes: readonly RuleOutcomeRecord[] | undefined
  /** A step about no pote: the labels, and not the potes' fichas or rules. */
  labelsOnly?: boolean
}) {
  if (preparations.length === 0) return null
  const many = preparations.length > 1
  const labelled = preparations.filter((one) => (one.labelLines?.length ?? 0) > 0)

  return (
    <div className="flex flex-col gap-2">
      {labelled.length > 0 && (
        <Fold summary="Rótulo">
          {labelled.map((one) => (
            <Part key={one.preparation.id} title={many ? one.title : undefined}>
              <LabelPrint lines={one.labelLines!} />
            </Part>
          ))}
        </Fold>
      )}

      {!labelsOnly && <PreparationContext {...{ preparations, sheet, outcomes, many }} />}
    </div>
  )
}

/** The ficha and the rules verified, for a step about a pote. */
function PreparationContext({
  preparations,
  sheet,
  outcomes,
  many,
}: {
  preparations: readonly ContextPreparation[]
  sheet: Sheet | undefined
  outcomes: readonly RuleOutcomeRecord[] | undefined
  many: boolean
}) {
  return (
    <>
      {sheet !== undefined &&
        (sheet.available ? (
          <Fold summary="Ficha de produção">
            {preparations.map((one) => (
              <Part key={one.preparation.id} title={many ? one.title : undefined}>
                <ProductionSheetContent preparation={one.preparation} sheet={sheet} />
              </Part>
            ))}
          </Fold>
        ) : (
          <p className="text-tSmall text-text-weak m-0">
            A ficha de produção não pôde ser lida do ERP para esta conferência.
          </p>
        ))}

      {outcomes === undefined ? (
        <p className="text-tSmall text-text-weak m-0">
          Regras operacionais: sem registro das regras avaliadas.
        </p>
      ) : (
        <Fold summary="Regras operacionais verificadas">
          {preparations.map((one) => (
            <Part key={one.preparation.id} title={many ? one.title : undefined}>
              <RuleOutcomeList lines={ruleOutcomesFor(outcomes, one.preparation.id)} />
            </Part>
          ))}
        </Fold>
      )}
    </>
  )
}

function Fold({ summary, children }: { summary: string; children: ReactNode }) {
  return (
    <details className="border-stroke-weak rounded-base border px-4 py-3">
      <summary className="text-tSmall text-text-weak cursor-pointer select-none">{summary}</summary>
      <div className="mt-2 flex flex-col gap-4">{children}</div>
    </details>
  )
}

function Part({ title, children }: { title: string | undefined; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      {title !== undefined && <span className="text-tPSemi text-text-strong">{title}</span>}
      {children}
    </div>
  )
}
